import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RotateCcw, ChevronDown } from "lucide-react";
import { FleetPulseFilters } from "./types";
import { safeArr } from "./helpers";

type FleetPulsePeriodFilters = FleetPulseFilters & {
  years?: string[];
  months?: string[];
};

interface Props {
  filters: FleetPulsePeriodFilters;
  setFilters: (f: FleetPulsePeriodFilters) => void;
  vehicleOptions: string[];
  driverOptions: { id: string; label: string }[];
  regionOptions: string[];
  yearOptions: string[];
  monthOptions?: { value: string; label: string }[];
  onReset: () => void;
}

const DEFAULT_MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function normaliseSelection(values?: string[]) {
  if (!values || values.length === 0) return ["all"];
  return values;
}

function getYearLabel(values?: string[]) {
  const selected = normaliseSelection(values);
  if (selected.includes("all")) return "All years";
  if (selected.length === 1) return selected[0];
  return `${selected.length} years selected`;
}

function getMonthLabel(values?: string[], months = DEFAULT_MONTHS) {
  const selected = normaliseSelection(values);
  if (selected.includes("all")) return "All months";
  if (selected.length === 1) {
    return months.find((m) => m.value === selected[0])?.label || selected[0];
  }
  return `${selected.length} months selected`;
}

const FleetPulseFiltersBar: React.FC<Props> = ({
  filters,
  setFilters,
  vehicleOptions,
  driverOptions,
  regionOptions,
  yearOptions,
  monthOptions,
  onReset,
}) => {
  const months = safeArr(monthOptions).length ? monthOptions! : DEFAULT_MONTHS;

  const update = (patch: Partial<FleetPulsePeriodFilters>) =>
    setFilters({ ...filters, ...patch });

  const selectedYears = normaliseSelection(filters.years);
  const selectedMonths = normaliseSelection(filters.months);

  const isYearChecked = (year: string) => {
    if (year === "all") return selectedYears.includes("all");
    return !selectedYears.includes("all") && selectedYears.includes(year);
  };

  const isMonthChecked = (month: string) => {
    if (month === "all") return selectedMonths.includes("all");
    return !selectedMonths.includes("all") && selectedMonths.includes(month);
  };

  const toggleYear = (year: string) => {
    if (year === "all") {
      update({ years: ["all"], year: "all" });
      return;
    }
    const current = selectedYears.includes("all") ? [] : selectedYears;
    const next = current.includes(year)
      ? current.filter((y) => y !== year)
      : [...current, year];
    update({
      years: next.length ? next.slice().sort() : ["all"],
      year: next.length === 1 ? next[0] : "all",
    });
  };

  const toggleMonth = (month: string) => {
    if (month === "all") {
      update({ months: ["all"], month: "all" });
      return;
    }
    const current = selectedMonths.includes("all") ? [] : selectedMonths;
    const next = current.includes(month)
      ? current.filter((m) => m !== month)
      : [...current, month];
    update({
      months: next.length ? next.slice().sort() : ["all"],
      month: next.length === 1 ? next[0] : "all",
    });
  };

  return (
    <Card className="bg-card border border-border">
      <CardContent className="pt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {/* Years checkbox dropdown */}
          <div>
            <Label className="text-xs text-muted-foreground">Years</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between dark-input font-normal"
                >
                  <span className="truncate">{getYearLabel(filters.years)}</span>
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <div className="max-h-72 overflow-y-auto p-2 space-y-1">
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer">
                    <Checkbox
                      checked={isYearChecked("all")}
                      onCheckedChange={() => toggleYear("all")}
                    />
                    <span className="text-sm">All years</span>
                  </label>
                  <div className="h-px bg-border my-1" />
                  {safeArr(yearOptions).map((year) => (
                    <label
                      key={year}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={isYearChecked(year)}
                        onCheckedChange={() => toggleYear(year)}
                      />
                      <span className="text-sm">{year}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Single or multiple years
            </div>
          </div>

          {/* Months checkbox dropdown */}
          <div>
            <Label className="text-xs text-muted-foreground">Months</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between dark-input font-normal"
                >
                  <span className="truncate">
                    {getMonthLabel(filters.months, months)}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="start">
                <div className="max-h-72 overflow-y-auto p-2 space-y-1">
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer">
                    <Checkbox
                      checked={isMonthChecked("all")}
                      onCheckedChange={() => toggleMonth("all")}
                    />
                    <span className="text-sm">All months</span>
                  </label>
                  <div className="h-px bg-border my-1" />
                  {months.map((month) => (
                    <label
                      key={month.value}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                    >
                      <Checkbox
                        checked={isMonthChecked(month.value)}
                        onCheckedChange={() => toggleMonth(month.value)}
                      />
                      <span className="text-sm">{month.label}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Compare same months year-on-year
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Vehicle</Label>
            <Select
              value={filters.vehicleId}
              onValueChange={(v) => update({ vehicleId: v })}
            >
              <SelectTrigger className="dark-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {safeArr(vehicleOptions).map((vehicle) => (
                  <SelectItem key={vehicle} value={vehicle}>
                    {vehicle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Driver</Label>
            <Select
              value={filters.driverId}
              onValueChange={(v) => update({ driverId: v })}
            >
              <SelectTrigger className="dark-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {safeArr(driverOptions).map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>
                    {driver.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Region</Label>
            <Select
              value={filters.region}
              onValueChange={(v) => update({ region: v })}
            >
              <SelectTrigger className="dark-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {safeArr(regionOptions).map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Fleet scope</Label>
            <Select
              value={filters.fleetScope}
              onValueChange={(v) =>
                update({ fleetScope: v as FleetPulseFilters["fleetScope"] })
              }
            >
              <SelectTrigger className="dark-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All fleet data</SelectItem>
                <SelectItem value="current">Current fleet only</SelectItem>
                <SelectItem value="historical">Historical only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">
              Driver scope
            </Label>
            <Select
              value={filters.driverScope}
              onValueChange={(v) =>
                update({ driverScope: v as FleetPulseFilters["driverScope"] })
              }
            >
              <SelectTrigger className="dark-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                <SelectItem value="current">Current drivers only</SelectItem>
                <SelectItem value="historical">Historical only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3 pt-5">
            <Switch
              checked={filters.hoistOnly}
              onCheckedChange={(v) => update({ hoistOnly: !!v })}
            />
            <Label className="text-sm">Hoist only</Label>
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReset}
              className="gap-2"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FleetPulseFiltersBar;
